import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { AccountStage } from "../../constants/accountStages";
import CustomerAccountService, {
  AccountSourceApi,
} from "../../services/CustomerAccountService";
import LeadService from "../../services/LeadService";
import { LeadStatus } from "../../constants/leadStatus";

const fieldStyle = {
  backgroundColor: "#f4f5f7",
  border: "1px solid #dee2e6",
  borderRadius: 6,
  fontFamily: "'Inria Serif', Georgia, serif",
  fontSize: "0.9rem",
};

const labelStyle = {
  fontFamily: "'Inria Serif', Georgia, serif",
  fontSize: "0.85rem",
  marginBottom: 4,
};

function parseInitialValue(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function toText(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email ?? "").trim());
}

function normalizePhoneDigits(phone) {
  return String(phone ?? "").replace(/\D/g, "");
}

function isValidPhone(phone) {
  const length = normalizePhoneDigits(phone).length;
  return length >= 10 && length <= 12;
}

function leadToForm(lead) {
  return {
    accountName: toText(lead?.company),
    contact: toText(lead?.name),
    phone: normalizePhoneDigits(lead?.phoneNumber ?? lead?.phone),
    email: toText(lead?.email),
    sourceLabel: "Qualified Lead",
    initialValue: "0",
    notes: toText(lead?.notes),
  };
}

const ConvertToAccountModal = ({ show, lead, onClose, onComplete }) => {
  const [form, setForm] = useState(leadToForm(null));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    if (!show || !lead) return;
    setForm(leadToForm(lead));
    setErrors({});
    setSubmitError("");
    setSaving(false);
    setSkipping(false);
  }, [show, lead]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.accountName.trim()) next.accountName = "Account name is required";
    if (!form.contact.trim()) next.contact = "Contact is required";

    const email = form.email.trim();
    const phone = form.phone.trim();

    if (!email) next.email = "Email is required";
    else if (!isValidEmail(email)) next.email = "Enter a valid email";

    if (!phone) next.phone = "Phone number is required";
    else if (!isValidPhone(phone)) {
      next.phone = "Enter a valid 10-digit phone number";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const updateLeadToCustomer = async (notes) => {
    const leadData = {
      leadId: lead.leadId,
      leadsStatus: LeadStatus.CUSTOMER,
      notes: notes ?? lead.notes,
    };
    const response = await LeadService.updateLead(leadData);
    if (response?.data?.success === false) {
      throw new Error(response?.data?.message || "Failed to update lead status.");
    }
  };

  const handleSkip = async () => {
    if (!lead) return;
    setSkipping(true);
    setSubmitError("");
    try {
      await updateLeadToCustomer(form.notes.trim() || lead.notes);
      onComplete?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setSubmitError(
        err?.response?.data?.message || err?.message || "Failed to update lead.",
      );
    } finally {
      setSkipping(false);
    }
  };

  const handleCreate = async () => {
    if (!lead || !validate()) return;
    setSaving(true);
    setSubmitError("");
    try {
      const amount = parseInitialValue(form.initialValue);
      const payload = {
        accountName: form.accountName.trim(),
        primaryContact: form.contact.trim(),
        companyEmail: toText(form.email),
        phoneNumber: normalizePhoneDigits(form.phone),
        source: AccountSourceApi.QUALIFIED_LEAD,
        stage: AccountStage.NEW_ACCOUNT,
        initialValue: amount,
        convertedFromLead: "",
        notes: form.notes.trim(),
        isDeleted: false,
      };

      const resp = await CustomerAccountService.createCustomerAccount(payload);
      const success = resp?.data?.success !== false;
      if (!success) {
        throw new Error(resp?.data?.message || "Failed to create account.");
      }

      await updateLeadToCustomer(form.notes.trim());
      onComplete?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setSubmitError(
        err?.response?.data?.message || err?.message || "Failed to create account.",
      );
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || skipping;

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : onClose}
      centered
      contentClassName="border-0"
      style={{ fontFamily: "'Inria Serif', Georgia, serif" }}
    >
      <Modal.Header closeButton={!busy} className="border-0 pb-0">
        <Modal.Title
          style={{ fontFamily: "'Inria Serif', Georgia, serif", fontWeight: 700 }}
        >
          <span className="me-2" aria-hidden>
            🎉
          </span>
          Convert to Account?
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Account Name</Form.Label>
            <Form.Control
              value={form.accountName}
              onChange={(e) => update("accountName", e.target.value)}
              placeholder="Urban Realty Developers"
              style={fieldStyle}
              isInvalid={Boolean(errors.accountName)}
              disabled={busy}
            />
            <Form.Control.Feedback type="invalid">{errors.accountName}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Contact</Form.Label>
            <Form.Control
              value={form.contact}
              onChange={(e) => update("contact", e.target.value)}
              placeholder="Pooja Singh"
              style={fieldStyle}
              isInvalid={Boolean(errors.contact)}
              disabled={busy}
            />
            <Form.Control.Feedback type="invalid">{errors.contact}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Phone <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={form.phone}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                update("phone", digits);
              }}
              placeholder="9876543210"
              inputMode="numeric"
              style={fieldStyle}
              isInvalid={Boolean(errors.phone)}
              disabled={busy}
            />
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="contact@company.com"
              style={fieldStyle}
              isInvalid={Boolean(errors.email)}
              disabled={busy}
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Source</Form.Label>
            <Form.Control
              value={form.sourceLabel}
              readOnly
              style={{ ...fieldStyle, backgroundColor: "#eef0f2" }}
              disabled
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Initial Value (optional)</Form.Label>
            <InputGroup>
              <InputGroup.Text style={{ ...fieldStyle, borderRight: 0 }}>₹</InputGroup.Text>
              <Form.Control
                value={form.initialValue}
                onChange={(e) => update("initialValue", e.target.value)}
                placeholder="0"
                style={{ ...fieldStyle, borderLeft: 0 }}
                disabled={busy}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label style={labelStyle}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Add any notes about this account..."
              style={fieldStyle}
              disabled={busy}
            />
          </Form.Group>

          {submitError ? (
            <div className="text-danger small mt-2">{submitError}</div>
          ) : null}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
        <Button
          variant="light"
          className="border px-3"
          style={{ fontFamily: "'Inria Serif', Georgia, serif" }}
          onClick={handleSkip}
          disabled={busy}
        >
          {skipping ? "Skipping…" : "Skip for now"}
        </Button>
        <Button
          variant="primary"
          className="px-3"
          style={{
            fontFamily: "'Inria Serif', Georgia, serif",
            backgroundColor: "#0d6efd",
            borderColor: "#0d6efd",
          }}
          onClick={handleCreate}
          disabled={busy}
        >
          {saving ? "Creating…" : "Create Account →"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConvertToAccountModal;
